import { useState, useCallback, useEffect, useMemo } from 'react'
import { ArrowLeft, Sparkles, AlertTriangle, Download } from 'lucide-react'
import { arrayMove } from '@dnd-kit/sortable'
import { cn } from '@/lib/utils'
import { valueDesignApi } from '@/lib/api/client'
import { useAIStore } from '@/stores/aiStore'
import { useSkerpaContextStore } from '@/stores/skerpaContextStore'
import { useSkerpaPageContext } from '@/hooks/useSkerpaPageContext'
import { useEntityCrudListener } from '@/hooks/useEntityListener'
import { STATUS_STYLES } from './shared'
import { nextId, getRelieverPainIds, getCreatorGainIds } from './PainGainRows'
import {
  computeLayout, buildPainReliefLinks, buildOutcomeCreatorLinks,
  painLabel, reliefLabel, outcomeLabel, creatorLabel,
} from './valueDesignLayout'
import { ValueDesignSpread } from './ValueDesignSpread'
import type {
  ValuePropositionCanvas, VPCData, VPCPainReliever, VPCGainCreator,
  PersonaWorkshopSession, PersonaData, JtbdData,
} from '@ispot/shared'
import { migratePersonaData } from '@ispot/shared'

interface Pain    { id: string; description: string; severity?: string }
interface Outcome { id: string; description: string; importance?: string }

interface Props {
  accountId: string
  projectId: string
  vpc: ValuePropositionCanvas
  session: PersonaWorkshopSession | null
  jtbd: JtbdData | null
  onBack: () => void
  onUpdate: (vpc: ValuePropositionCanvas) => void
}

/** Apply the VPC's per-canvas display order to a JTBD-owned list (pains/gains).
 *  Items not present in the order are appended in their original position. */
function applyOrder<T extends { id: string }>(items: T[], order: string[] | undefined): T[] {
  if (!order || order.length === 0) return items
  const byId = new Map(items.map(it => [it.id, it]))
  const seen = new Set<string>()
  const out: T[] = []
  for (const id of order) {
    const it = byId.get(id)
    if (it) { out.push(it); seen.add(id) }
  }
  for (const it of items) if (!seen.has(it.id)) out.push(it)
  return out
}

export function ValueDesignCanvas({
  accountId, projectId, vpc, session, jtbd, onBack, onUpdate,
}: Props) {
  const [saving, setSaving] = useState(false)
  const [aiLoading, setAiLoading] = useState<string | null>(null)
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const { apiKey } = useAIStore()

  const personaData = session ? migratePersonaData(session.personaData as PersonaData) : null
  const setActiveResource = useSkerpaContextStore(s => s.setActiveResource)

  useEffect(() => {
    setActiveResource(vpc.id)
    return () => setActiveResource(null)
  }, [vpc.id, setActiveResource])

  const refetchVpc = useCallback(async () => {
    try {
      const updated = await valueDesignApi.get(accountId, projectId, vpc.id)
      onUpdate(updated)
    } catch { /* ignore */ }
  }, [accountId, projectId, vpc.id, onUpdate])
  useEntityCrudListener('PainReliever', refetchVpc)
  useEntityCrudListener('GainCreator', refetchVpc)

  const data = vpc.data as VPCData

  // Apply per-VPC display order to JTBD-owned pains/outcomes.
  const pains    = useMemo<Pain[]>   (() => applyOrder(jtbd?.pains ?? [], data.painOrder), [jtbd?.pains, data.painOrder])
  const outcomes = useMemo<Outcome[]>(() => applyOrder(jtbd?.gains ?? [], data.gainOrder), [jtbd?.gains, data.gainOrder])
  const relievers = data.painRelievers
  const creators  = data.gainCreators

  // Skerpa side-panel context
  const skerpaContext = useMemo(() => [
    `Value Proposition Canvas: ${vpc.status || 'Draft'}`,
    personaData ? `Persona: ${personaData.name || 'Unnamed'}` : null,
    jtbd ? `JTBD: ${jtbd.functional || jtbd.title || ''}` : null,
    pains.length ? `Pains (${pains.length}):\n${pains.map((p, i) => `  ${i + 1}. ${p.description} [${p.severity || 'medium'}]`).join('\n')}` : null,
    outcomes.length ? `Gains (${outcomes.length}):\n${outcomes.map((g, i) => `  ${i + 1}. ${g.description} [${g.importance || 'expected'}]`).join('\n')}` : null,
    relievers.length ? `Pain Relievers (${relievers.length}):\n${relievers.map((r, i) => `  ${i + 1}. ${r.description}`).join('\n')}` : null,
    creators.length ? `Gain Creators (${creators.length}):\n${creators.map((c, i) => `  ${i + 1}. ${c.description}`).join('\n')}` : null,
    data.valueProposition ? `Value Proposition: ${data.valueProposition}` : null,
  ].filter(Boolean).join('\n\n'), [vpc.status, personaData, jtbd, pains, outcomes, relievers, creators, data.valueProposition])
  useSkerpaPageContext(skerpaContext)

  const layout = useMemo(
    () => computeLayout(pains.length, relievers.length, outcomes.length, creators.length),
    [pains.length, relievers.length, outcomes.length, creators.length],
  )
  const { byPain, byReliever } = useMemo(() => buildPainReliefLinks(pains, relievers), [pains, relievers])
  const { byOutcome, byCreator } = useMemo(() => buildOutcomeCreatorLinks(outcomes, creators), [outcomes, creators])

  const linkedSet = useMemo(() => {
    if (!hoveredId) return new Set<string>()
    const idx = parseInt(hoveredId.slice(1), 10) - 1
    if (Number.isNaN(idx)) return new Set<string>()
    if (hoveredId.startsWith('P')) return new Set(byPain[idx]?.map(j => reliefLabel(j)) ?? [])
    if (hoveredId.startsWith('R')) return new Set(byReliever[idx]?.map(i => painLabel(i)) ?? [])
    if (hoveredId.startsWith('O')) return new Set(byOutcome[idx]?.map(j => creatorLabel(j)) ?? [])
    if (hoveredId.startsWith('G')) return new Set(byCreator[idx]?.map(i => outcomeLabel(i)) ?? [])
    return new Set<string>()
  }, [hoveredId, byPain, byReliever, byOutcome, byCreator])

  // ----- Mutations ---------------------------------------------------------
  const saveData = useCallback(async (dataUpdate: Partial<VPCData>) => {
    setSaving(true)
    try {
      const updated = await valueDesignApi.update(accountId, projectId, vpc.id, { data: dataUpdate })
      onUpdate(updated)
    } finally { setSaving(false) }
  }, [accountId, projectId, vpc.id, onUpdate])

  const handleAiDraft = async (block: string) => {
    if (!apiKey) return
    setAiLoading(block)
    try {
      const result = await valueDesignApi.aiDraft(accountId, projectId, vpc.id, block, apiKey)
      if (result?.data) {
        const updated = await valueDesignApi.update(accountId, projectId, vpc.id, {
          data: result.data, createdBy: 'ai_drafted',
        })
        onUpdate(updated)
      }
    } finally { setAiLoading(null) }
  }

  const addReliever = (painId: string) => {
    const r: VPCPainReliever = { id: nextId('pr'), painId, painIds: [painId], description: '' }
    saveData({ painRelievers: [...relievers, r] })
  }
  const updateReliever = (i: number, description: string) => {
    const u = [...relievers]; u[i] = { ...u[i], description }; saveData({ painRelievers: u })
  }
  const removeReliever = (i: number) =>
    saveData({ painRelievers: relievers.filter((_, j) => j !== i) })
  const toggleReliever = (relieverIdx: number, painId: string) => {
    const r = relievers[relieverIdx]
    const current = getRelieverPainIds(r)
    const next = current.includes(painId) ? current.filter(id => id !== painId) : [...current, painId]
    const updated = [...relievers]
    updated[relieverIdx] = { ...r, painIds: next, painId: next[0] || r.painId }
    saveData({ painRelievers: updated })
  }

  const addCreator = (gainId: string) => {
    const c: VPCGainCreator = { id: nextId('gc'), gainId, gainIds: [gainId], description: '' }
    saveData({ gainCreators: [...creators, c] })
  }
  const updateCreator = (i: number, description: string) => {
    const u = [...creators]; u[i] = { ...u[i], description }; saveData({ gainCreators: u })
  }
  const removeCreator = (i: number) =>
    saveData({ gainCreators: creators.filter((_, j) => j !== i) })
  const toggleCreator = (creatorIdx: number, gainId: string) => {
    const c = creators[creatorIdx]
    const current = getCreatorGainIds(c)
    const next = current.includes(gainId) ? current.filter(id => id !== gainId) : [...current, gainId]
    const updated = [...creators]
    updated[creatorIdx] = { ...c, gainIds: next, gainId: next[0] || c.gainId }
    saveData({ gainCreators: updated })
  }

  // ----- Reorder handler --------------------------------------------------
  const handleReorder = (
    kind: 'pain' | 'outcome' | 'reliever' | 'creator',
    activeId: string, overId: string,
  ) => {
    if (kind === 'pain') {
      const list = pains.map(p => p.id)
      const oldI = list.indexOf(activeId), newI = list.indexOf(overId)
      if (oldI < 0 || newI < 0) return
      saveData({ painOrder: arrayMove(list, oldI, newI) })
    } else if (kind === 'outcome') {
      const list = outcomes.map(o => o.id)
      const oldI = list.indexOf(activeId), newI = list.indexOf(overId)
      if (oldI < 0 || newI < 0) return
      saveData({ gainOrder: arrayMove(list, oldI, newI) })
    } else if (kind === 'reliever') {
      const oldI = relievers.findIndex(r => (r.id ?? '') === activeId)
      const newI = relievers.findIndex(r => (r.id ?? '') === overId)
      if (oldI < 0 || newI < 0) return
      saveData({ painRelievers: arrayMove(relievers, oldI, newI) })
    } else if (kind === 'creator') {
      const oldI = creators.findIndex(c => (c.id ?? '') === activeId)
      const newI = creators.findIndex(c => (c.id ?? '') === overId)
      if (oldI < 0 || newI < 0) return
      saveData({ gainCreators: arrayMove(creators, oldI, newI) })
    }
  }

  const status = STATUS_STYLES[vpc.status] || STATUS_STYLES.draft

  return (
    <div className="flex flex-col h-[calc(100vh-48px)] bg-warm-50 overflow-y-auto">
      {/* Doc header */}
      <div className="flex items-center justify-between px-12 py-4 border-b border-border-subtle bg-bg flex-shrink-0">
        <div className="flex flex-col gap-1 min-w-0">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.06em] text-text-muted">
            <button onClick={onBack}
                    className="inline-flex items-center justify-center w-[22px] h-[22px] border border-border-subtle rounded text-text-secondary hover:bg-warm-100"
                    aria-label="Back">
              <ArrowLeft className="w-3 h-3" />
            </button>
            <span>Value design</span>
            <span className="opacity-50">·</span>
            <span className={cn('inline-flex items-center gap-1.5 px-1.5 py-px rounded text-text-secondary', status.className)}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--color-accent-500)' }} />
              {status.label}
            </span>
            {vpc.isOrphaned && (
              <span className="inline-flex items-center gap-1 text-amber-700">
                <AlertTriangle className="w-3 h-3" /> Orphaned
              </span>
            )}
          </div>
          <h1 className="text-[22px] font-display font-semibold leading-tight text-text truncate max-w-[760px]">
            {jtbd?.title || 'Untitled job to be done'}
          </h1>
          <div className="text-[12.5px] text-text-secondary truncate">
            For <span className="font-medium text-text">{personaData?.name || 'Unnamed persona'}</span>
            {personaData?.role ? `, ${personaData.role}` : ''}
            {personaData?.archetype ? <span className="text-text-muted"> &nbsp;·&nbsp; <em>{personaData.archetype}</em></span> : null}
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {saving && <span className="text-[11px] text-text-muted">Saving…</span>}
          <button className="inline-flex items-center gap-1.5 h-7 px-3 rounded text-[12px] font-medium text-text-secondary border border-border-subtle hover:bg-warm-100">
            <Download className="w-3 h-3" /> Export
          </button>
          <button
            onClick={() => handleAiDraft('all')}
            disabled={!!aiLoading || !apiKey}
            title={apiKey ? undefined : 'Set API key in Settings first'}
            className="inline-flex items-center gap-1.5 h-7 px-3 rounded text-[12px] font-medium text-white transition-colors disabled:opacity-50"
            style={{ background: 'var(--color-accent-500)' }}
          >
            <Sparkles className="w-3 h-3" />
            {aiLoading === 'all' ? 'Drafting…' : 'AI draft all'}
          </button>
        </div>
      </div>

      {/* Spread */}
      <ValueDesignSpread
        persona={personaData} jtbd={jtbd}
        pains={pains} outcomes={outcomes} relievers={relievers} creators={creators}
        productName="Your solution"
        valueProposition={data.valueProposition || ''}
        layout={layout}
        byPain={byPain} byReliever={byReliever} byOutcome={byOutcome} byCreator={byCreator}
        hoveredId={hoveredId} linkedSet={linkedSet} onHover={setHoveredId}
        onChangeValueProp={v => saveData({ valueProposition: v })}
        onAiDraft={handleAiDraft}
        onAddReliever={addReliever} onUpdateReliever={updateReliever}
        onRemoveReliever={removeReliever} onToggleReliever={toggleReliever}
        onAddCreator={addCreator} onUpdateCreator={updateCreator}
        onRemoveCreator={removeCreator} onToggleCreator={toggleCreator}
        onReorder={handleReorder}
        aiLoading={aiLoading} apiKeyAvailable={!!apiKey}
      />

      {/* Footer */}
      <div className="flex items-center justify-between px-12 py-3 border-t border-border-subtle bg-warm-50 text-[11.5px] text-text-muted flex-shrink-0">
        <div className="flex gap-5 items-center">
          <span>
            <strong className="font-semibold text-text">{byPain.filter(arr => arr.length > 0).length}</strong>
            /{pains.length} pains tackled
          </span>
          <span>
            <strong className="font-semibold text-text">{byOutcome.filter(arr => arr.length > 0).length}</strong>
            /{outcomes.length} outcomes delivered
          </span>
          {(pains.length - byPain.filter(a => a.length > 0).length + outcomes.length - byOutcome.filter(a => a.length > 0).length) > 0 && (
            <span style={{ color: 'var(--color-accent-500)' }}>
              ● {(pains.length - byPain.filter(a => a.length > 0).length + outcomes.length - byOutcome.filter(a => a.length > 0).length)} unaddressed
            </span>
          )}
        </div>
        <button className="inline-flex items-center gap-1.5 h-7 px-3 rounded text-[12px] font-medium text-text-secondary border border-border-subtle hover:bg-warm-100">
          Promote to PRD →
        </button>
      </div>
    </div>
  )
}
